// v1.0.0 - 2026-01-28 - Member form modal (E1-US2 create, E1-US4 edit)

import { useState, useEffect } from 'react'
import { useAuthStore } from '@/lib/stores/authStore'
import { api } from '@/lib/api/client'
import type { Member, MemberFormData } from '@/types'
import type { MemberType } from '@/lib/ti-calculator/types'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'

interface MemberFormModalProps {
  isOpen: boolean
  onClose: () => void
  onSuccess: () => void
  editingMember?: Member | null
}

export function MemberFormModal({ isOpen, onClose, onSuccess, editingMember }: MemberFormModalProps) {
  const { user } = useAuthStore()
  const isEdit = !!editingMember

  // Form state
  const [name, setName] = useState('')
  const [email, setEmail] = useState('')
  const [type, setType] = useState<MemberType>('consumer')
  const [isCompany, setIsCompany] = useState(false)
  const [cf, setCf] = useState('')
  const [pod, setPod] = useState('')
  const [censimp, setCensimp] = useState('')
  const [pnrrPercent, setPnrrPercent] = useState(0)

  // UI state
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // Populate form when editing
  useEffect(() => {
    if (editingMember) {
      setName(editingMember.name)
      setEmail(editingMember.email)
      setType(editingMember.type)
      setIsCompany(editingMember.is_company)
      setCf(editingMember.cf || '')
      setPod(editingMember.pod || '')
      setCensimp(editingMember.censimp || '')
      setPnrrPercent(editingMember.pnrr_percent)
    } else {
      // Reset form for new member
      setName('')
      setEmail('')
      setType('consumer')
      setIsCompany(false)
      setCf('')
      setPod('')
      setCensimp('')
      setPnrrPercent(0)
    }
    setError(null)
  }, [editingMember, isOpen])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!user?.cer_id) return

    setIsSubmitting(true)
    setError(null)

    const formData: MemberFormData = {
      name: name.trim(),
      email: email.trim().toLowerCase(),
      type,
      is_company: isCompany,
      pnrr_percent: pnrrPercent,
      cf: cf.trim() || undefined,
      pod: pod.trim() || undefined,
      censimp: censimp.trim() || undefined,
    }

    let response
    if (isEdit && editingMember) {
      response = await api.updateMember(user.cer_id, editingMember.id, formData)
    } else {
      response = await api.createMember(user.cer_id, formData)
    }

    setIsSubmitting(false)

    if (response.success) {
      onSuccess()
      onClose()
    } else {
      setError('error' in response ? response.error : 'Errore nel salvataggio')
    }
  }

  const showProducerFields = type === 'producer' || type === 'prosumer'

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <Card className="w-full max-w-lg max-h-[90vh] overflow-y-auto">
        <CardHeader>
          <CardTitle>{isEdit ? 'Modifica Membro' : 'Nuovo Membro'}</CardTitle>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            {error && (
              <div className="rounded-lg bg-[var(--color-bg-error)] p-3 text-sm text-[var(--color-error)]">
                {error}
              </div>
            )}

            {/* Nome */}
            <div>
              <label className="mb-1 block text-sm font-medium text-[var(--color-text-primary)]">
                Nome *
              </label>
              <Input
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Nome completo"
                required
              />
            </div>

            {/* Email */}
            <div>
              <label className="mb-1 block text-sm font-medium text-[var(--color-text-primary)]">
                Email *
              </label>
              <Input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="email@esempio.it"
                required
              />
            </div>

            {/* Tipo */}
            <div>
              <label className="mb-1 block text-sm font-medium text-[var(--color-text-primary)]">
                Tipo *
              </label>
              <select
                value={type}
                onChange={(e) => setType(e.target.value as MemberType)}
                className="h-10 w-full rounded-lg border border-[var(--color-border)] bg-[var(--color-bg-primary)] px-3 text-sm"
                required
              >
                <option value="consumer">Consumatore</option>
                <option value="producer">Produttore</option>
                <option value="prosumer">Prosumer</option>
              </select>
            </div>

            {/* Is Company */}
            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="isCompany"
                checked={isCompany}
                onChange={(e) => setIsCompany(e.target.checked)}
                className="h-4 w-4 rounded border-[var(--color-border)]"
              />
              <label htmlFor="isCompany" className="text-sm text-[var(--color-text-primary)]">
                Azienda (persona giuridica)
              </label>
            </div>

            {/* CF / PIVA */}
            <div>
              <label className="mb-1 block text-sm font-medium text-[var(--color-text-primary)]">
                {isCompany ? 'Partita IVA' : 'Codice Fiscale'}
              </label>
              <Input
                value={cf}
                onChange={(e) => setCf(e.target.value.toUpperCase())}
                placeholder={isCompany ? '12345678901 (11 cifre)' : 'RSSMRA85M01H501Z (16 caratteri)'}
                maxLength={isCompany ? 11 : 16}
              />
              <p className="mt-1 text-xs text-[var(--color-text-tertiary)]">
                {isCompany ? '11 cifre numeriche' : '16 caratteri alfanumerici'}
              </p>
            </div>

            {/* POD */}
            <div>
              <label className="mb-1 block text-sm font-medium text-[var(--color-text-primary)]">
                POD (Punto di Prelievo)
              </label>
              <Input
                value={pod}
                onChange={(e) => setPod(e.target.value.toUpperCase())}
                placeholder="IT001E12345678"
              />
              <p className="mt-1 text-xs text-[var(--color-text-tertiary)]">
                Formato: IT + 3 cifre + E + 8-9 caratteri
              </p>
            </div>

            {/* Producer-only fields */}
            {showProducerFields && (
              <>
                {/* CENSIMP */}
                <div>
                  <label className="mb-1 block text-sm font-medium text-[var(--color-text-primary)]">
                    CENSIMP (Codice Impianto)
                  </label>
                  <Input
                    value={censimp}
                    onChange={(e) => setCensimp(e.target.value.toUpperCase())}
                    placeholder="Codice censimento impianto"
                  />
                </div>

                {/* PNRR Percent */}
                <div>
                  <label className="mb-1 block text-sm font-medium text-[var(--color-text-primary)]">
                    Percentuale PNRR
                  </label>
                  <div className="flex items-center gap-2">
                    <Input
                      type="number"
                      value={pnrrPercent}
                      onChange={(e) => setPnrrPercent(Math.min(100, Math.max(0, parseInt(e.target.value) || 0)))}
                      min={0}
                      max={100}
                      className="w-24"
                    />
                    <span className="text-sm text-[var(--color-text-secondary)]">%</span>
                  </div>
                  <p className="mt-1 text-xs text-[var(--color-text-tertiary)]">
                    Percentuale impianto finanziato con fondi PNRR (riduce TI del 50%)
                  </p>
                </div>
              </>
            )}

            {/* Actions */}
            <div className="flex justify-end gap-3 pt-4">
              <Button type="button" variant="outline" onClick={onClose} disabled={isSubmitting}>
                Annulla
              </Button>
              <Button type="submit" isLoading={isSubmitting}>
                {isEdit ? 'Salva Modifiche' : 'Crea Membro'}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  )
}
